import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getConsent, setConsent } from "../lib/analytics";

/**
 * Minimalist cookie / consent banner — RGPD & Loi 25 (Québec) compliant.
 * Renders once when the user has never made a choice. Hides after a decision.
 */
export default function CookieBanner() {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        // Show only on first visit (no decision yet)
        if (!getConsent()) {
            // Slight delay so it doesn't fight the hero animation
            const t = setTimeout(() => setVisible(true), 1200);
            return () => clearTimeout(t);
        }
    }, []);

    const accept = () => {
        setConsent(true);
        setVisible(false);
    };
    const refuse = () => {
        setConsent(false);
        setVisible(false);
    };

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    data-testid="cookie-banner"
                    initial={{ y: 60, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 60, opacity: 0 }}
                    transition={{ duration: 0.35, ease: "easeOut" }}
                    className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-md z-[100]"
                >
                    <div className="border border-scd bg-elev/95 backdrop-blur-lg rounded-sm p-5 shadow-2xl">
                        <div className="flex items-start gap-3">
                            <span className="mt-1 inline-block w-2 h-2 rounded-full bg-yellow shrink-0" />
                            <div className="flex-1 min-w-0">
                                <p className="text-cream text-sm leading-relaxed">
                                    On mesure l&apos;audience de manière anonyme
                                    pour améliorer le site. Rien n&apos;est
                                    partagé avec un tiers.
                                </p>
                                <div className="mt-4 flex flex-wrap gap-2">
                                    <button
                                        onClick={accept}
                                        data-testid="cookie-accept"
                                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-yellow text-[#272727] text-sm font-semibold hover:bg-green hover:text-[#faf7f1] transition-colors"
                                    >
                                        Accepter
                                    </button>
                                    <button
                                        onClick={refuse}
                                        data-testid="cookie-refuse"
                                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-scd text-cream/85 text-sm hover:border-yellow hover:text-yellow transition-colors"
                                    >
                                        Refuser
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
